import React, { useEffect, useState } from "react";
import { SignalPulse } from "../components/SignalPulse";
import { useNavigate } from "react-router-dom";
import { Loader2, ArrowLeft, Heart, CreditCard, Bitcoin, Copy, Check, ShieldCheck } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import api from "../lib/api";

const PRESETS = [5, 15, 50];

const isInAppBrowser = () =>
  /Instagram|FBAN|FBAV|FB_IAB|Line|Snapchat|Pinterest|Twitter|TikTok/i.test(navigator.userAgent || "");

const CryptoCard = ({ label, ticker, address }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(address);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };
  return (
    <div className="border border-white/10 bg-[#121212] p-5" data-testid={`crypto-card-${ticker.toLowerCase()}`}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="font-heading text-base text-white">{label}</div>
          <div className="font-body text-[10px] tracking-[0.25em] uppercase text-[#A855F7]">{ticker}</div>
        </div>
        <div className="bg-white p-2 rounded-sm shrink-0">
          <QRCodeSVG value={address} size={84} level="M" />
        </div>
      </div>
      <div className="flex items-center gap-2 border border-white/15 px-3 py-2">
        <span className="font-mono text-xs text-zinc-300 break-all flex-1" data-testid={`crypto-address-${ticker.toLowerCase()}`}>{address}</span>
        <button onClick={copy} data-testid={`crypto-copy-${ticker.toLowerCase()}`} className="text-zinc-500 hover:text-[#14A89A] shrink-0 transition-colors">
          {copied ? <Check size={15} className="text-[#14A89A]" /> : <Copy size={15} />}
        </button>
      </div>
    </div>
  );
};

export default function Donate() {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [tab, setTab] = useState("card");
  const [amount, setAmount] = useState(15);
  const [custom, setCustom] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [statusMsg, setStatusMsg] = useState(null); // {type, text}
  const [inApp] = useState(isInAppBrowser());
  const [copiedLink, setCopiedLink] = useState(false);

  const copyDonateLink = () => {
    navigator.clipboard.writeText(`${window.location.origin}/donate`);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 1800);
  };

  useEffect(() => {
    api.get("/donate/config").then(({ data }) => setConfig(data)).catch(() => setConfig({ stripe_enabled: false, crypto: {} }));
  }, []);

  // Returning from Stripe, poll the payment status.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) return;
    setStatusMsg({ type: "pending", text: "Confirming your donation..." });
    let attempts = 0;
    const poll = async () => {
      if (attempts >= 6) { setStatusMsg({ type: "error", text: "Still processing, check your email for a receipt." }); return; }
      attempts += 1;
      try {
        const { data } = await api.get(`/donate/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatusMsg({ type: "success", text: `Thank you, your $${Number(data.amount).toFixed(2)} donation fuels the build. 🌱` });
          window.history.replaceState({}, "", "/donate");
          return;
        }
        if (data.status === "expired") { setStatusMsg({ type: "error", text: "That checkout session expired. Please try again." }); return; }
        setTimeout(poll, 2000);
      } catch {
        setStatusMsg({ type: "error", text: "Could not confirm payment status." });
      }
    };
    poll();
  }, []);

  const chosenAmount = () => {
    if (custom !== "") { const n = parseFloat(custom); return isNaN(n) ? 0 : n; }
    return amount;
  };

  const donate = async () => {
    const amt = chosenAmount();
    if (!amt || amt < 1) { setError("Please choose or enter an amount of at least $1."); return; }
    setError(""); setBusy(true);
    try {
      const { data } = await api.post("/donate/checkout", { amount: amt, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not start checkout. Please try again.");
      setBusy(false);
    }
  };

  const crypto = config?.crypto || {};
  const hasCrypto = crypto.btc || crypto.eth || crypto.atom;

  return (
    <div className="max-w-2xl mx-auto px-4 md:px-6 py-10" data-testid="donate-page">
      <button onClick={() => navigate("/feed")} data-testid="donate-back" className="flex items-center gap-2 text-zinc-400 hover:text-white font-body text-sm mb-8 transition-colors">
        <ArrowLeft size={16} /> Back to The Signal
      </button>

      <div className="mb-8">
        <p className="font-body text-[10px] tracking-[0.4em] uppercase text-[#14A89A] mb-3">Support the build</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-medium tracking-tight text-[#14A89A] mb-4">Fuel Sovereign Signal</h1>
        <p className="font-body text-base text-zinc-300 leading-relaxed">
          Sovereign Signal is built and funded by Amy alone. Every donation goes straight into keeping us private, sovereign, and growing - and helps move the build forward faster. There's no obligation here; only gratitude.
        </p>
      </div>

      {inApp && (
        <div data-testid="inapp-donate-notice" className="border border-[#A855F7]/40 bg-[#A855F7]/[0.08] p-4 mb-8 font-body text-sm text-zinc-200">
          <p className="mb-3 leading-relaxed">You're viewing inside an app's browser (like Instagram), which can block secure card checkout. To donate by card, open this page in <span className="text-white">Safari</span>: tap the <span className="text-white">•••</span> (or share) menu at the top → <span className="text-white">"Open in external browser."</span></p>
          <button onClick={copyDonateLink} data-testid="copy-donate-link" className="inline-flex items-center gap-2 border border-white/20 px-4 py-2 text-xs hover:bg-white/5 transition-colors">
            {copiedLink ? <><Check size={14} className="text-[#14A89A]" /> Link copied, paste in Safari</> : <><Copy size={14} /> Copy donate link</>}
          </button>
        </div>
      )}

      {statusMsg && (
        <div data-testid="donate-status" className={`border p-4 mb-8 font-body text-sm ${statusMsg.type === "success" ? "border-[#14A89A]/40 bg-[#14A89A]/[0.08] text-[#14A89A]" : statusMsg.type === "error" ? "border-red-500/40 bg-red-500/[0.06] text-red-300" : "border-white/15 text-zinc-300"}`}>
          {statusMsg.type === "pending" && <Loader2 size={14} className="animate-spin inline mr-2 align-middle" />}
          {statusMsg.text}
        </div>
      )}

      {config === null ? (
        <div className="flex justify-center py-16"><SignalPulse size={44} /></div>
      ) : (
        <>
          <div className="flex gap-2 mb-7">
            {config.stripe_enabled && (
              <button onClick={() => setTab("card")} data-testid="donate-tab-card" className={`flex items-center gap-2 px-5 py-2.5 font-body text-xs tracking-wider uppercase border transition-colors ${tab === "card" ? "border-[#14A89A] text-[#14A89A]" : "border-white/15 text-zinc-400 hover:text-white"}`}>
                <CreditCard size={15} /> Card
              </button>
            )}
            <button onClick={() => setTab("crypto")} data-testid="donate-tab-crypto" className={`flex items-center gap-2 px-5 py-2.5 font-body text-xs tracking-wider uppercase border transition-colors ${tab === "crypto" ? "border-[#A855F7] text-[#A855F7]" : "border-white/15 text-zinc-400 hover:text-white"}`}>
              <Bitcoin size={15} /> Crypto
            </button>
          </div>

          {tab === "card" && config.stripe_enabled && (
            <div className="space-y-6" data-testid="donate-card-panel">
              <div className="grid grid-cols-3 gap-3">
                {PRESETS.map((p) => (
                  <button key={p} onClick={() => { setAmount(p); setCustom(""); }} data-testid={`donate-amount-${p}`} className={`py-4 font-heading text-xl border transition-colors ${custom === "" && amount === p ? "border-[#14A89A] text-[#14A89A] bg-[#14A89A]/[0.06]" : "border-white/15 text-zinc-300 hover:border-white/30"}`}>
                    ${p}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-3 border border-white/15 px-4 focus-within:border-[#14A89A] transition-colors">
                <span className="font-heading text-lg text-zinc-500">$</span>
                <input value={custom} onChange={(e) => setCustom(e.target.value.replace(/[^0-9.]/g, ""))} placeholder="Other amount" data-testid="donate-custom-input" inputMode="decimal" className="flex-1 bg-transparent py-3.5 text-base text-white placeholder:text-zinc-600 focus:outline-none font-body" />
              </div>
              {error && <p className="font-body text-sm text-red-400" data-testid="donate-error">{error}</p>}
              <button onClick={donate} disabled={busy} data-testid="donate-submit" className="w-full bg-[#14A89A] text-[#0A0A0A] font-medium px-6 py-4 hover:bg-[#2BC4B5] transition-colors flex items-center justify-center gap-2 font-body disabled:opacity-40">
                {busy ? <Loader2 className="animate-spin" size={16} /> : <><Heart size={16} /> <span>Donate{chosenAmount() ? ` $${chosenAmount()}` : ""} with card</span></>}
              </button>
              <p className="font-body text-xs text-zinc-600 flex items-center gap-1.5"><ShieldCheck size={13} className="text-[#14A89A]" /> Securely processed by Stripe. Sovereign Signal never sees your card details.</p>
            </div>
          )}

          {tab === "crypto" && (
            <div className="space-y-4" data-testid="donate-crypto-panel">
              <p className="font-body text-sm text-zinc-400 leading-relaxed">
                Donate directly, peer to peer, with no processor in between - the most sovereign way to give. Send to any address below.
              </p>
              {!hasCrypto ? (
                <div className="border border-white/10 p-6 text-center" data-testid="crypto-empty">
                  <p className="font-body text-sm text-zinc-500">Crypto donation addresses haven't been set up yet.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {crypto.btc && <CryptoCard label="Bitcoin" ticker="BTC" address={crypto.btc} />}
                  {crypto.eth && <CryptoCard label="Ethereum" ticker="ETH" address={crypto.eth} />}
                  {crypto.atom && <CryptoCard label="Cosmos" ticker="ATOM" address={crypto.atom} />}
                </div>
              )}
              <p className="font-body text-xs text-zinc-600">Always double-check the address before sending. Crypto transfers are irreversible.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
